import type { Platform, PlatformScope } from './types';
import { platformsInScope } from './types';

/**
 * A tab on the consolidated Performance surface. `platforms` is the set the tab
 * has data for; the tab is disabled (with `disabledReason`) when the current
 * scope includes none of them — e.g. Keywords is Google-only and disables under
 * Meta scope (#5971).
 */
export interface ScopedTabDef {
  id: string;
  label: string;
  platforms: Platform[];
  disabledReason?: string;
}

export type TabAvailability = { available: true } | { available: false; reason: string };

/** Whether a tab is usable under the given scope, with an explainer when not. */
export function tabAvailability(tab: ScopedTabDef, scope: PlatformScope): TabAvailability {
  const inScope = platformsInScope(scope);
  if (tab.platforms.some((p) => inScope.includes(p))) return { available: true };
  return { available: false, reason: tab.disabledReason ?? `Not available in the ${scope} view.` };
}

/**
 * The unified tab set (prototype: Overview, Campaigns, Creative & Ads, Keywords
 * & Terms, Audience, Budget, Activity). Order is the display order.
 */
export const CONSOLIDATED_TABS: ScopedTabDef[] = [
  { id: 'overview', label: 'Overview', platforms: ['google', 'meta'] },
  { id: 'campaigns', label: 'Campaigns', platforms: ['google', 'meta'] },
  { id: 'creative-ads', label: 'Creative & Ads', platforms: ['google', 'meta'] },
  {
    id: 'keywords-terms',
    label: 'Keywords & Terms',
    platforms: ['google'],
    disabledReason: 'Keywords apply to Google Search only — Meta has no query-level entity.',
  },
  { id: 'audience', label: 'Audience', platforms: ['google', 'meta'] },
  // Budget + Activity are out of scope for this release — re-enable by
  // uncommenting these (the panels + TabPanel/renderConsolidatedWidget wiring
  // are still in place). #5971
  // { id: 'budget', label: 'Budget', platforms: ['google', 'meta'] },
  // {
  //   id: 'activity',
  //   label: 'Activity',
  //   platforms: ['google'],
  //   disabledReason: 'Change history is available for Google campaigns.',
  // },
];

export const CONSOLIDATED_TAB_IDS = CONSOLIDATED_TABS.map((t) => t.id);

/**
 * The tab to actually render: the requested one if it's available in the current
 * scope, otherwise Overview (always available). Keeps the surface from rendering
 * a disabled panel when the scope switch strands the active tab (e.g. sitting on
 * Keywords, then switching to Meta).
 */
export function resolveActiveTab(requested: string, scope: PlatformScope): string {
  const tab = CONSOLIDATED_TABS.find((t) => t.id === requested);
  return tab && tabAvailability(tab, scope).available ? requested : 'overview';
}
